import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ChevronLeft, ChevronRight, Heart, Mic, Moon, Paintbrush, RotateCcw, Sparkles, Users, Volume2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLook } from "@/context/LookContext";
import { KBTN, mediaUrl, pageSound, reducedMotion } from "@/lib/story";
import Recorder from "@/components/story/Recorder";

function Img({ path, alt, testid }) {
  const [src, setSrc] = useState(null);
  useEffect(() => { let on = true; setSrc(null); if (path) mediaUrl(path).then((u) => on && setSrc(u)).catch(() => {}); return () => { on = false; }; }, [path]);
  return src ? <img src={src} alt={alt} className="h-full w-full object-cover" data-testid={testid} /> : <div className="flex h-full w-full items-center justify-center bg-[rgba(var(--glow-kids),0.08)] text-5xl">📖</div>;
}

const CONFETTI = Array.from({ length: 18 }, (_, n) => ({ x: (n * 41) % 100, d: (n % 6) * 0.15, c: ["#f472b6", "#facc15", "#60a5fa", "#a78bfa"][n % 4] }));

function TheEnd({ book, end }) {
  return (
    <div className="relative flex h-full flex-col items-center justify-center gap-4 overflow-hidden p-6 text-center" data-testid="the-end-page">
      {CONFETTI.map((p, n) => <Sparkles key={n} className="end-pop absolute h-6 w-6" style={{ left: `${p.x}%`, top: `${10 + ((n * 23) % 70)}%`, color: p.c, animationDelay: `${p.d}s` }} />)}
      <p className="end-pop font-display text-5xl font-extrabold text-q-text">The End</p>
      <p className="text-q-muted">{book.title}</p>
      <div className="relative z-10 flex flex-wrap justify-center gap-3">
        <button className={`${KBTN} bg-[rgb(var(--glow-kids))] text-white`} onClick={end.again} data-testid="end-read-again"><RotateCcw className="mr-2 inline h-5 w-5" />Read again</button>
        <button className={`${KBTN} border border-q-line`} onClick={end.pick} data-testid="end-pick-another"><BookOpen className="mr-2 inline h-5 w-5" />Pick another book</button>
        <button className={`${KBTN} border border-q-line`} onClick={end.bedtime} data-testid="end-bedtime-music"><Moon className="mr-2 inline h-5 w-5" />Bedtime music</button>
      </div>
    </div>
  );
}

const Starry = () => (
  <div className="relative h-full w-full overflow-hidden" style={{ background: "radial-gradient(circle at 30% 25%, #4c3b7a, #1e1b4b 60%, #0f0c29)" }}>
    {Array.from({ length: 26 }, (_, n) => <span key={n} className="absolute rounded-full bg-white/80" style={{ left: `${(n * 37) % 100}%`, top: `${(n * 53) % 100}%`, width: 2 + (n % 3), height: 2 + (n % 3), opacity: 0.4 + (n % 4) * 0.15 }} />)}
    <span className="absolute right-[18%] top-[18%] h-12 w-12 rounded-full bg-[#fef3c7]/80 shadow-[0_0_40px_14px_rgba(254,243,199,0.25)]" />
  </div>
);

function Page({ book, i, words, hi, onRepaint, end }) {
  if (i >= book.pages.length) return <TheEnd book={book} end={end} />;
  const p = book.pages[i];
  const tokens = words?.length ? words.map((w) => w.w) : p.text.split(/\s+/);
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid={`reader-page-${i + 1}`}>
      <div className="relative min-h-0 flex-[3] overflow-hidden rounded-2xl">
        {p.image_failed ? (
          <div className="relative h-full" data-testid={`page-image-failed-${i + 1}`}>
            <Starry />
            {onRepaint && <button className={`${KBTN} absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 text-white`} onClick={() => onRepaint(i)} data-testid="repaint-btn"><Paintbrush className="mr-2 inline h-4 w-4" /> Paint this page again</button>}
          </div>
        ) : <Img path={p.image_url} alt="" testid={`page-image-${i + 1}`} />}
      </div>
      <p className="mt-3 flex-[1] overflow-y-auto font-display text-[22px] leading-relaxed text-q-text sm:text-2xl" data-testid={`page-text-${i + 1}`}>
        {tokens.map((t, n) => <span key={n} className={`rounded px-0.5 transition-colors ${n === hi ? "bg-[rgba(var(--glow-kids),0.35)] text-white" : ""}`} data-testid={n === hi ? "word-highlight" : undefined}>{t} </span>)}
      </p>
      <p className="text-right text-xs text-q-faint">{i + 1}</p>
    </div>
  );
}

export default function Reader({ hub, bookId, voices, togetherOn, onClose }) {
  const { look } = useLook();
  const [book, setBook] = useState(null);
  const [i, setI] = useState(0);
  const [wide, setWide] = useState(window.innerWidth >= 1024);
  const [turn, setTurn] = useState(null);
  const [mode, setMode] = useState("self");
  const [playing, setPlaying] = useState(false);
  const [words, setWords] = useState(null);
  const [hi, setHi] = useState(-1);
  const [aiLabel, setAiLabel] = useState(false);
  const [auto, setAuto] = useState(true);
  const [recording, setRecording] = useState(false);
  const [resume, setResume] = useState(null);
  const nav = useNavigate();
  const audio = useRef(new Audio());
  const timer = useRef(null);
  const base = `/organizations/${hub.id}/story`;
  const load = useCallback(() => api.get(`${base}/books/${bookId}`).then(({ data }) => { setBook(data); return data; }), [base, bookId]);
  useEffect(() => { load().then((d) => { const p = d.progress_page || 0; if (p > 0 && p < d.pages.length) setResume(p); }).catch((e) => { toast.error(errMsg(e)); onClose(); }); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const r = () => setWide(window.innerWidth >= 1024); window.addEventListener("resize", r); return () => window.removeEventListener("resize", r); }, []);
  const step = wide ? 2 : 1;
  const total = book ? book.pages.length + 1 : 0;
  const stopAudio = () => { audio.current.pause(); clearTimeout(timer.current); setPlaying(false); setHi(-1); };
  const go = useCallback((dir) => {
    if (!book) return;
    const next = Math.max(0, Math.min(total - 1, i + dir * step));
    if (next === i) return;
    setTurn({ dir, key: Date.now() }); pageSound(look?.sound_muted);
    setI(next); setWords(null); setHi(-1);
    api.put(`${base}/books/${bookId}/progress`, { page: Math.min(next, book.pages.length) }).catch(() => {});
  }, [book, i, step, total, look, base, bookId]);
  const turnTo = (n) => {
    const same = wide && Math.floor(n / 2) === Math.floor(i / 2);
    if (!same) { setTurn({ dir: 1, key: Date.now() }); pageSound(look?.sound_muted); }
    setI(n);
    api.put(`${base}/books/${bookId}/progress`, { page: n }).catch(() => {});
  };
  useEffect(() => () => stopAudio(), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  }, [go, onClose]);
  const sourceFor = async (n) => {
    const rec = book.recordings.find((r) => r.id === mode);
    if (rec && rec.pages[String(n)]) return { url: rec.pages[String(n)].url, words: null, ai: false };
    const voice = rec || mode === "self" ? "luna" : mode;
    const { data } = await api.get(`${base}/books/${bookId}/narration/${n}`, { params: { voice }, timeout: 60000 });
    return { ...data, ai: !!rec };
  };
  const play = useCallback(async (n) => {
    if (n >= book.pages.length) { setPlaying(false); return; }
    setPlaying(true);
    try {
      const src = await sourceFor(n);
      setWords(src.words); setAiLabel(src.ai);
      audio.current.src = await mediaUrl(src.url);
      audio.current.ontimeupdate = () => { const t = audio.current.currentTime; setHi(src.words ? src.words.findLastIndex((w) => w.s <= t) : -1); };
      audio.current.onended = () => { setHi(-1); if (auto && n + 1 < book.pages.length) timer.current = setTimeout(() => { turnTo(n + 1); play(n + 1); }, 1500); else setPlaying(false); };
      await audio.current.play();
      if (n + 1 < book.pages.length) sourceFor(n + 1).then((s) => mediaUrl(s.url)).catch(() => {});
    } catch (e) { setPlaying(false); setMode("self"); toast.error(e.response?.data?.detail || "Reading voice isn't available right now. You can still read yourself."); }
  }, [book, mode, auto, wide, i]); // eslint-disable-line react-hooks/exhaustive-deps
  const fav = async () => { const { data } = await api.put(`${base}/books/${bookId}/favorite`, { on: !book.favorite }); setBook({ ...book, favorite: data.favorite }); };
  const repaint = async (n) => { toast("Painting…"); try { await api.post(`${base}/books/${bookId}/pages/${n}/repaint`, {}, { timeout: 120000 }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const swipe = useRef(null);
  if (!book) return <div className="fixed inset-0 z-[80] bg-q-bg" />;
  const anim = turn ? (reducedMotion() ? "reader-fade" : turn.dir > 0 ? "reader-curl-next" : "reader-curl-prev") : "";
  const pages = wide ? [i - (i % 2), i - (i % 2) + 1].filter((n) => n <= book.pages.length) : [i];
  const endOnly = pages.length === 1 && pages[0] >= book.pages.length;
  const end = {
    again: () => { stopAudio(); setTurn({ dir: -1, key: Date.now() }); setI(0); api.put(`${base}/books/${bookId}/progress`, { page: 0 }).catch(() => {}); },
    pick: onClose,
    bedtime: () => { stopAudio(); nav(`/h/${hub.id}/kids/bedtime`); },
  };
  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-q-bg" data-testid="reader"
      onTouchStart={(e) => { swipe.current = e.touches[0].clientX; }} onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (swipe.current ?? 0); if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); }}>
      <div className="flex flex-wrap items-center gap-2 border-b border-q-line px-3 py-2">
        <button className={`${KBTN} w-14 px-0`} aria-label="Close" onClick={onClose} data-testid="reader-close-btn"><X className="mx-auto h-6 w-6" /></button>
        <p className="min-w-0 flex-1 basis-[calc(100%-4.5rem)] truncate lg:basis-0 font-display text-lg font-extrabold text-q-text" data-testid="reader-title">{book.title}</p>
        <select className="q-input min-h-[56px] max-w-[60vw]" value={mode} onChange={(e) => { stopAudio(); setMode(e.target.value); }} data-testid="reader-mode-select">
          <option value="self">Read myself</option>
          {voices.map((v) => <option key={v.id} value={v.id}>{`Read to me · ${v.name}`}</option>)}
          {book.recordings.map((r) => <option key={r.id} value={r.id}>{`Read by ${r.by}`}</option>)}
        </select>
        {mode !== "self" && <button className={`${KBTN} bg-[rgb(var(--glow-kids))] text-white`} onClick={() => (playing ? stopAudio() : play(Math.min(i, book.pages.length - 1)))} data-testid="reader-play-btn"><Volume2 className="mr-1 inline h-5 w-5" />{playing ? "Stop" : "Play"}</button>}
        <label className="flex min-h-[56px] items-center gap-2 text-sm text-q-muted"><input type="checkbox" className="h-6 w-6" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid="reader-autoturn" /> Auto-turn</label>
        <button className={`${KBTN} w-14 px-0`} aria-label="Favorite" onClick={fav} data-testid="reader-fav-btn"><Heart className={`mx-auto h-6 w-6 ${book.favorite ? "fill-[rgb(var(--glow-kids))] text-[rgb(var(--glow-kids))]" : ""}`} /></button>
        {hub.my_role !== "kid" && <button className={`${KBTN} border border-q-line`} onClick={() => setRecording(true)} data-testid="record-voice-btn"><Mic className="mr-1 inline h-5 w-5" /> Record my voice</button>}
        <button className={`${KBTN} border border-q-line opacity-60`} disabled={!togetherOn} title={togetherOn ? "" : "Read together is coming soon"} data-testid="read-together-btn"><Users className="mr-1 inline h-5 w-5" />{togetherOn ? "Read together" : "Read together is coming soon"}</button>
      </div>
      {aiLabel && playing && <p className="px-4 pt-2 text-xs font-bold text-[rgb(var(--glow-kids))]" data-testid="ai-voice-label">AI voice</p>}
      <div className="relative flex min-h-0 flex-1 items-stretch gap-2 p-3 [perspective:2000px] sm:p-6">
        <button className="absolute inset-y-0 left-0 z-10 w-[18%] min-w-[56px]" aria-label="Previous page" onClick={() => go(-1)} data-testid="reader-prev-zone" />
        <button className="absolute inset-y-0 right-0 z-10 w-[18%] min-w-[56px]" aria-label="Next page" onClick={() => go(1)} data-testid="reader-next-zone" />
        <div key={turn?.key} className={`grid min-h-0 flex-1 gap-4 ${wide && !endOnly ? "grid-cols-2" : "grid-cols-1"} ${anim}`} data-testid={wide ? "reader-spread" : "reader-single"}>
          {pages.map((n) => <div key={n} className="min-h-0 rounded-3xl border border-q-line bg-q-raised p-3 shadow-2xl sm:p-5"><Page book={book} i={n} words={n === i ? words : null} hi={n === i ? hi : -1} onRepaint={book.can_repaint ? repaint : null} end={end} /></div>)}
        </div>
      </div>
      <div className="flex items-center justify-center gap-3 pb-4">
        <button className={`${KBTN} w-14 px-0`} aria-label="Previous" onClick={() => go(-1)} data-testid="reader-prev-btn"><ChevronLeft className="mx-auto h-7 w-7" /></button>
        <span className="text-sm text-q-muted" data-testid="reader-position">{Math.min(i + 1, book.pages.length)} / {book.pages.length}</span>
        <button className={`${KBTN} w-14 px-0`} aria-label="Next" onClick={() => go(1)} data-testid="reader-next-btn"><ChevronRight className="mx-auto h-7 w-7" /></button>
      </div>
      {resume != null && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 p-4" data-testid="resume-prompt">
          <div className="w-full max-w-sm space-y-3 rounded-3xl bg-q-raised p-6 text-center shadow-2xl">
            <p className="font-display text-xl font-extrabold text-q-text">{book.title}</p>
            <button className={`${KBTN} w-full bg-[rgb(var(--glow-kids))] text-white`} onClick={() => { setI(resume); setResume(null); }} data-testid="resume-keep-reading">{`Keep reading from page ${resume + 1}`}</button>
            <button className={`${KBTN} w-full border border-q-line`} onClick={() => { setI(0); setResume(null); api.put(`${base}/books/${bookId}/progress`, { page: 0 }).catch(() => {}); }} data-testid="resume-start-over">Start over</button>
          </div>
        </div>
      )}
      {recording && <Recorder hubId={hub.id} book={book} onClose={() => setRecording(false)} onPublished={load} />}
    </div>
  );
}
